import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ContractsPage from "./pages/ContractsPage";
import ContractDetailPage from "./pages/ContractDetailPage";
import CustomersPage from "./pages/CustomersPage";
import UsersPage from "./pages/UsersPage";
import DocumentsPage from "./pages/DocumentsPage";
import AuditLogPage from "./pages/AuditLogPage";
import ReportsPage from "./pages/ReportsPage";
import NotificationsPage from "./pages/NotificationsPage";
// Workflow Builder Console
import WorkflowConsole from "./pages/WorkflowConsole";
import ProductWorkflowManager from "./pages/ProductWorkflowManager";
import ProductWorkflowBuilderPage from "./pages/ProductWorkflowBuilderPage";
import CatalogPage from "./pages/CatalogPage";
import InventoryPage from "./pages/InventoryPage";
// Quotes / Preventivi
import QuotesListPage from "./pages/QuotesListPage";
import QuoteDetailPage from "./pages/QuoteDetailPage";
// Store HVAC
import StoreQuotesPage from "./pages/StoreQuotesPage";
import StoreQuoteDetailPage from "./pages/StoreQuoteDetailPage";
import StorePromotionsPage from "./pages/StorePromotionsPage";
import StoreProductsAdminPage from "./pages/StoreProductsAdminPage";
import StoreBulkEditPage from "./pages/StoreBulkEditPage";
import StorePdfBrandingPage from "./pages/StorePdfBrandingPage";

// Public route - redirects to dashboard if already logged in
function PublicRoute({ children }) {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }
    
    if (user) {
        return <Navigate to="/" replace />;
    }
    
    return children;
}

function AppRoutes() {
    return (
        <Routes>
            {/* Public routes */}
            <Route 
                path="/login" 
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                } 
            />

            {/* Protected routes */}
            <Route 
                path="/" 
                element={
                    <ProtectedRoute>
                        <Layout>
                            <DashboardPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/contratti" 
                element={
                    <ProtectedRoute>
                        <Layout>
                            <ContractsPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />

            {/* Preventivi */}
            <Route 
                path="/preventivi" 
                element={
                    <ProtectedRoute pvAllowed>
                        <Layout>
                            <QuotesListPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/preventivi/:id" 
                element={
                    <ProtectedRoute pvAllowed>
                        <Layout>
                            <QuoteDetailPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/contratti/:id" 
                element={
                    <ProtectedRoute>
                        <Layout>
                            <ContractDetailPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/clienti" 
                element={
                    <ProtectedRoute>
                        <Layout>
                            <CustomersPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/documenti" 
                element={
                    <ProtectedRoute>
                        <Layout>
                            <DocumentsPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/report" 
                element={
                    <ProtectedRoute>
                        <Layout>
                            <ReportsPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/notifiche" 
                element={
                    <ProtectedRoute>
                        <Layout>
                            <NotificationsPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />

            {/* Admin routes - Workflow Builder Console */}
            <Route 
                path="/workflow-console" 
                element={
                    <ProtectedRoute adminOnly>
                        <Layout>
                            <WorkflowConsole />
                        </Layout>
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/workflow-console/product/:productId" 
                element={
                    <ProtectedRoute adminOnly>
                        <Layout>
                            <ProductWorkflowManager />
                        </Layout>
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/workflow-console/product/:productId/workflow/:workflowId" 
                element={
                    <ProtectedRoute adminOnly>
                        <Layout>
                            <ProductWorkflowBuilderPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/listino-materiali" 
                element={
                    <ProtectedRoute logisticsAllowed>
                        <Layout>
                            <CatalogPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/inventario" 
                element={
                    <ProtectedRoute logisticsAllowed>
                        <Layout>
                            <InventoryPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />

            {/* Legacy redirects for backwards compatibility */}
            <Route path="/prodotti" element={<Navigate to="/workflow-console?tab=products" replace />} />
            <Route path="/step-library" element={<Navigate to="/workflow-console?tab=step-library" replace />} />
            <Route path="/workflow-builder" element={<Navigate to="/workflow-console?tab=products" replace />} />

            {/* Store HVAC Module */}
            <Route 
                path="/store" 
                element={
                    <ProtectedRoute storeAllowed>
                        <Layout>
                            <StoreQuotesPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/store/quotes/:quoteId" 
                element={
                    <ProtectedRoute storeAllowed>
                        <Layout>
                            <StoreQuoteDetailPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/store/promozioni" 
                element={
                    <ProtectedRoute catalogManagerAllowed>
                        <Layout>
                            <StorePromotionsPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/store/prodotti" 
                element={
                    <ProtectedRoute catalogManagerAllowed>
                        <Layout>
                            <StoreProductsAdminPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/store/modifiche-rapide" 
                element={
                    <ProtectedRoute catalogManagerAllowed>
                        <Layout>
                            <StoreBulkEditPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />
            <Route path="/store/prodotti-excel" element={<Navigate to="/store/modifiche-rapide" replace />} />
            <Route 
                path="/store/branding-pdf" 
                element={
                    <ProtectedRoute adminOnly>
                        <Layout>
                            <StorePdfBrandingPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/utenti" 
                element={
                    <ProtectedRoute adminOnly>
                        <Layout>
                            <UsersPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/audit" 
                element={
                    <ProtectedRoute adminOnly>
                        <Layout>
                            <AuditLogPage />
                        </Layout>
                    </ProtectedRoute>
                } 
            />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRoutes />
                <Toaster position="top-right" richColors />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
